import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getFamousItems, getMenuItems } from '../services/api';
import { Button } from '../components/ui/button';
import { toast } from '../hooks/use-toast';
import SEO from '../components/SEO';
import OptimizedImage from '../components/OptimizedImage';

const Specials = () => {
  const [featuredSpecials, setFeaturedSpecials] = useState([]);
  const [allSpecialties, setAllSpecialties] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [limitedOffer, setLimitedOffer] = useState(null);
  const [winners, setWinners] = useState({ current_winner: null, past_winners: [] });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sectionTitles, setSectionTitles] = useState({});
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [featuredVideo, setFeaturedVideo] = useState(null);
  const videoRef = React.useRef(null);
  
  // Drawing form state
  const [entryType, setEntryType] = useState('customer');
  const [drawingForm, setDrawingForm] = useState({ 
    name: '', 
    email: '', 
    phone: '',
    smsConsent: false,
    comment: '',
    companyName: '',
    companyPhone: '',
    websiteUrl: '',
    position: '',
    bestContactMethod: 'email'
  });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [formLoadTime] = useState(Date.now());

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [famousData, specialtyData] = await Promise.all([
          getFamousItems(),
          getMenuItems('specialty')
        ]);
        
        setFeaturedSpecials(famousData);
        // Filter items to only show those meant for Specials page
        const specialsPageItems = specialtyData.filter(item => 
          !item.display_page || item.display_page === 'specials' || item.display_page === 'both'
        );
        setAllSpecialties(specialsPageItems);
        
        // Fetch section titles
        try {
          const titlesRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/settings/menu-sections/titles`);
          if (titlesRes.ok) {
            const titlesData = await titlesRes.json();
            setSectionTitles(titlesData);
          }
        } catch (e) {
          console.log('Section titles not available');
        }
        
        // Fetch promotions
        try {
          const promoRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/promotions`);
          if (promoRes.ok) {
            const promoData = await promoRes.json();
            setPromotions(promoData.filter(p => p.is_active));
          }
        } catch (e) {
          console.log('Promotions not available');
        }

        // Fetch Limited Time Offer
        try {
          const offerRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/specials-content/limited-offer`);
          if (offerRes.ok) {
            const offerData = await offerRes.json();
            if (offerData && offerData.is_active) {
              setLimitedOffer(offerData);
            }
          }
        } catch (e) {
          console.log('Limited offer not available');
        }

        // Fetch Winners
        try {
          const winnersRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/specials-content/winners`);
          if (winnersRes.ok) {
            setWinners(await winnersRes.json());
          }
        } catch (e) {
          console.log('Winners not available');
        }

        // Fetch Events
        try {
          const eventsRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/specials-content/events`);
          if (eventsRes.ok) {
            setEvents(await eventsRes.json());
          }
        } catch (e) {
          console.log('Events not available');
        }

        // Fetch Featured Video
        try {
          const videoRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/specials-content/featured-video`);
          if (videoRes.ok) {
            const videoData = await videoRes.json();
            if (videoData && videoData.is_active) {
              setFeaturedVideo(videoData);
            }
          }
        } catch (e) {
          console.log('Featured video not available');
        }
      } catch (error) {
        console.error('Error fetching specials:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Validation functions
  const containsHtml = (str) => {
    if (!str) return false;
    return /<[^>]*>/g.test(str);
  };

  const isFakePhone = (phone) => {
    if (!phone) return true;
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10) return true;
    if (digits.includes('555')) return true;
    if (new Set(digits).size <= 2) return true;
    return false;
  };

  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validateForm = () => {
    if (honeypot) return { valid: false, error: 'Spam detected' };
    if (Date.now() - formLoadTime < 3000) {
      return { valid: false, error: 'Please take your time filling out the form' };
    }
    if (!drawingForm.name.trim()) return { valid: false, error: 'Name is required' };
    if (!isValidEmail(drawingForm.email)) return { valid: false, error: 'Valid email is required' };
    if (isFakePhone(drawingForm.phone)) return { valid: false, error: 'Valid phone number is required (no 555 numbers)' };
    if (!drawingForm.smsConsent) return { valid: false, error: 'SMS consent is required to enter the drawing' };

    const fields = [drawingForm.name, drawingForm.email, drawingForm.phone, drawingForm.comment];
    for (const field of fields) {
      if (containsHtml(field)) return { valid: false, error: 'HTML is not allowed' };
    }

    if (entryType === 'vendor') {
      if (!drawingForm.companyName.trim()) return { valid: false, error: 'Company name is required' };
      if (isFakePhone(drawingForm.companyPhone)) return { valid: false, error: 'Valid company phone is required' };
      if (!drawingForm.websiteUrl.trim()) return { valid: false, error: 'Website URL is required' };
      if (!drawingForm.position.trim()) return { valid: false, error: 'Position is required' };
    }

    return { valid: true };
  };

  const handleDrawingSubmit = async (e) => {
    e.preventDefault();
    
    const validation = validateForm();
    if (!validation.valid) {
      toast({ title: 'Error', description: validation.error, variant: 'destructive' });
      return;
    }

    setFormSubmitting(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/drawings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: drawingForm.name,
          email: drawingForm.email,
          phone: drawingForm.phone,
          sms_consent: drawingForm.smsConsent,
          comment: drawingForm.comment || null,
          entry_type: entryType,
          company_name: entryType === 'vendor' ? drawingForm.companyName : null,
          company_phone: entryType === 'vendor' ? drawingForm.companyPhone : null,
          website_url: entryType === 'vendor' ? drawingForm.websiteUrl : null,
          position: entryType === 'vendor' ? drawingForm.position : null,
          best_contact_method: drawingForm.bestContactMethod
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Failed to submit entry');
      }

      toast({ title: '🎉 You\'re Entered!', description: 'Good luck in the weekly drawing!' });
      setDrawingForm({ 
        name: '', email: '', phone: '', smsConsent: false, comment: '',
        companyName: '', companyPhone: '', websiteUrl: '', position: '', bestContactMethod: 'email'
      });
      setEntryType('customer');
    } catch (error) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    } finally {
      setFormSubmitting(false);
    }
  };

  // Helper to get YouTube embed URL
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?]+)/);
    return match ? `https://www.youtube.com/embed/${match[1]}` : null;
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-32 flex items-center justify-center">
        <div className="text-4xl font-black">Loading specials...</div>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen pt-32"
      style={{
        backgroundImage: 'url(/images/misc/storefront-bg.jpg)',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundAttachment: 'fixed'
      }}
    >
      <SEO 
        title="Specials & Promotions"
        description="Check out our weekly specials, seasonal flavors, and limited-time promotions at 1st Ave ICP in Mineral Wells, TX. Fresh deals on ice cream, sundaes, and pastries!"
        keywords="ice cream specials Mineral Wells, dessert deals Texas, weekly specials ice cream shop, promotions Mineral Wells TX"
        canonical="/specials"
      />
      {/* Hero with Dripping Effect */}
      <section className="relative bg-pink-200 pt-16 pb-24 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <h1 
            className="text-5xl md:text-7xl font-bold"
            style={{ fontFamily: 'Rowdies, cursive' }}
          >
            Specials
          </h1>
          
          {/* Weekly Winner Badge - Only show if there's a winner */}
          {winners.current_winner && (
            <button
              onClick={() => document.getElementById('weekly-winners')?.scrollIntoView({ behavior: 'smooth' })}
              className="mt-6 inline-flex items-center gap-2 bg-gradient-to-r from-[#E91E8C] to-[#C4197A] text-white px-6 py-3 rounded-full font-bold shadow-lg hover:shadow-xl hover:scale-105 transition-all animate-pulse"
            >
              <span className="text-xl">🏆</span>
              <span>See Our Newest Weekly Winner!</span>
              <span className="text-xl">↓</span>
            </button>
          )}
        </div>
        
        {/* Dripping Effect */}
        <div className="absolute bottom-0 left-0 right-0 h-20">
          <svg className="w-full h-full" viewBox="0 0 1440 80" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path 
              fill="#EC4899" 
              d="M0,0 L48,8 C96,16 192,32 288,36 C384,40 480,32 576,28 C672,24 768,24 864,28 C960,32 1056,40 1152,40 C1248,40 1344,32 1392,28 L1440,24 L1440,80 L0,80 Z"
            />
            <path 
              fill="#ffffff" 
              d="M0,24 L60,28 C120,32 240,40 360,44 C480,48 600,48 720,44 C840,40 960,32 1080,32 C1200,32 1320,40 1380,44 L1440,48 L1440,80 L0,80 Z"
            />
            {/* Drips */}
            <ellipse cx="80" cy="52" rx="6" ry="28" fill="#EC4899" opacity="0.8"/>
            <ellipse cx="280" cy="56" rx="8" ry="24" fill="#EC4899" opacity="0.7"/>
            <ellipse cx="520" cy="54" rx="7" ry="26" fill="#EC4899" opacity="0.8"/>
            <ellipse cx="750" cy="58" rx="6" ry="22" fill="#EC4899" opacity="0.7"/>
            <ellipse cx="980" cy="53" rx="8" ry="27" fill="#EC4899" opacity="0.8"/>
            <ellipse cx="1180" cy="56" rx="7" ry="24" fill="#EC4899" opacity="0.7"/>
            <ellipse cx="1360" cy="54" rx="6" ry="26" fill="#EC4899" opacity="0.8"/>
          </svg>
        </div>
      </section>

      {/* Promotions & Announcements */}
      {promotions.length > 0 && (
        <section className="py-16 px-6">
          <div className="max-w-7xl mx-auto">
            <h2 
              className="text-4xl md:text-5xl font-bold text-center mb-12"
              style={{ fontFamily: 'Rowdies, cursive' }}
            >
              What&apos;s Happening
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {promotions.map((promo) => (
                <div
                  key={promo.id}
                  className="bg-white p-6 rounded-3xl shadow-lg hover:shadow-xl transition-all"
                >
                  {promo.youtube_url && getYouTubeEmbedUrl(promo.youtube_url) ? (
                    <div className="aspect-video rounded-xl overflow-hidden mb-4">
                      <iframe
                        width="100%"
                        height="100%"
                        src={getYouTubeEmbedUrl(promo.youtube_url)}
                        title={promo.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </div>
                  ) : promo.image_url && (
                    <OptimizedImage
                      src={promo.image_url}
                      alt={promo.title}
                      className="w-full h-48 object-cover rounded-xl mb-4"
                      width={400}
                      height={192}
                    />
                  )}
                  <div className="text-sm text-pink-600 font-bold mb-2">
                    {promo.promo_type === 'announcement' && '📢 Announcement'}
                    {promo.promo_type === 'contest' && '🏆 Contest'}
                    {promo.promo_type === 'limited_offer' && '⏰ Limited Time'}
                    {promo.promo_type === 'event' && '🎉 Event'}
                  </div>
                  <h3 className="text-xl font-black mb-2">{promo.title}</h3>
                  <p className="text-gray-600 text-sm whitespace-pre-line">{promo.description}</p>
                  {(promo.start_date || promo.end_date) && (
                    <p className="text-xs text-gray-400 mt-3">
                      📅 {promo.start_date && new Date(promo.start_date).toLocaleDateString()}
                      {promo.start_date && promo.end_date && ' - '}
                      {promo.end_date && new Date(promo.end_date).toLocaleDateString()}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Limited Time Offer Section */}
      {limitedOffer && limitedOffer.is_active && (
        <section className="py-16 px-6 relative z-10">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-4">
              <span className="inline-block bg-red-500 text-white px-4 py-1 rounded-full text-sm font-bold mb-4">
                LIMITED TIME OFFER
              </span>
              <h2 
                className="text-4xl md:text-5xl font-bold"
                style={{ fontFamily: 'Rowdies, cursive' }}
              >
                {limitedOffer.title}
              </h2>
            </div>
            {limitedOffer.description && (
              <div className="max-w-2xl mx-auto mb-8">
                <p className="text-lg text-gray-700 whitespace-pre-line">
                  {limitedOffer.description}
                </p>
              </div>
            )}
            
            {/* Offer Images with Captions */}
            {limitedOffer.images && limitedOffer.images.length > 0 && (
              <div className={`grid gap-4 sm:gap-6 md:gap-8 relative z-20 items-end ${
                limitedOffer.images.length === 1 ? 'max-w-sm sm:max-w-md mx-auto' :
                limitedOffer.images.length === 2 ? 'grid-cols-2 max-w-lg sm:max-w-2xl mx-auto' :
                'grid-cols-1 sm:grid-cols-3 max-w-sm sm:max-w-4xl md:max-w-5xl mx-auto'
              }`}>
                {limitedOffer.images.map((img, idx) => (
                  <div key={idx} className="flex flex-col h-full">
                    {/* Caption Above */}
                    {limitedOffer.caption_position === 'above' && limitedOffer.image_captions?.[idx] && (
                      <p className="text-center text-sm sm:text-base md:text-lg font-bold text-gray-800 mb-2 sm:mb-3" style={{ fontFamily: 'Rowdies, cursive' }}>
                        {limitedOffer.image_captions[idx]}
                      </p>
                    )}
                    
                    {/* Image - Responsive height container with bottom alignment */}
                    <div className="flex-1 flex items-end justify-center">
                      <div className="rounded-xl sm:rounded-2xl overflow-hidden shadow-lg sm:shadow-xl border-2 sm:border-4 border-white bg-white w-full">
                        <img
                          src={img}
                          alt={limitedOffer.image_captions?.[idx] || `${limitedOffer.title} ${idx + 1}`}
                          className="w-full h-auto object-contain max-h-[280px] sm:max-h-[350px] md:max-h-[400px] lg:max-h-[450px]"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    </div>
                    
                    {/* Caption Below */}
                    {limitedOffer.caption_position !== 'above' && limitedOffer.image_captions?.[idx] && (
                      <p className="text-center text-sm sm:text-base md:text-lg font-bold text-gray-800 mt-2 sm:mt-3" style={{ fontFamily: 'Rowdies, cursive' }}>
                        {limitedOffer.image_captions[idx]}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Events Gallery Section */}
      {events.length > 0 && (
        <section className="py-16 px-6 bg-gradient-to-b from-purple-50 to-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block bg-purple-500 text-white px-4 py-1 rounded-full text-sm font-bold mb-4">
                📸 EVENTS
              </span>
              <h2 
                className="text-4xl md:text-5xl font-bold"
                style={{ fontFamily: 'Rowdies, cursive' }}
              >
                Memories Made Here
              </h2>
            </div>
            
            <div className="space-y-12">
              {events.map((event) => (
                <div key={event.id} className="bg-white p-6 md:p-8 rounded-3xl shadow-lg">
                  <h3 className="text-2xl font-black mb-2">{event.title}</h3>
                  {event.description && (
                    <p className="text-gray-600 mb-6 whitespace-pre-line">{event.description}</p>
                  )}
                  {event.images && event.images.length > 0 && (
                    <div className={`grid gap-4 ${
                      event.images.length === 1 ? 'grid-cols-1 max-w-md' :
                      event.images.length === 2 ? 'grid-cols-2' :
                      event.images.length <= 4 ? 'grid-cols-2 md:grid-cols-4' :
                      'grid-cols-2 md:grid-cols-3 lg:grid-cols-6'
                    }`}>
                      {event.images.map((img, idx) => (
                        <div key={idx} className="aspect-square rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all">
                          <OptimizedImage
                            src={img}
                            alt={`${event.title} ${idx + 1}`}
                            className="w-full h-full object-cover hover:scale-105 transition-transform"
                            width={200}
                            height={200}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Specials */}
      <section className="py-16 px-6 mt-16">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            {featuredSpecials.map((special) => (
              <div
                key={special.id}
                className="bg-gradient-to-br from-pink-50 to-teal-50 p-12 rounded-3xl shadow-xl hover:shadow-2xl transition-all"
              >
                <div className="text-center">
                  <OptimizedImage
                    src={special.image_url}
                    alt={special.name}
                    className="w-64 h-64 mx-auto object-contain mb-8"
                    width={256}
                    height={256}
                  />
                  <div className="flex items-center justify-center gap-2 mb-4">
                    <OptimizedImage
                      src="/images/icons/star.png"
                      alt="star"
                      className="w-10 h-10"
                      width={40}
                      height={40}
                      lazy={false}
                    />
                    <h2 className="text-4xl font-black">Famous</h2>
                    <OptimizedImage
                      src="/images/icons/star.png"
                      alt="star"
                      className="w-10 h-10"
                      width={40}
                      height={40}
                      lazy={false}
                    />
                  </div>
                  <h3 className="text-3xl font-black mb-6">{special.name}</h3>
                  <p className="text-lg text-gray-700 mb-8">
                    One of our most popular items! Made with love and the finest ingredients.
                  </p>
                  {/* TODO: Add CTA button - options: "Call to Order", "View Menu", "See Prices" */}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* All Specialties - Only show if there are items */}
      {allSpecialties.length > 0 && (
        <section className="py-16 px-6 pb-24">
          <div className="max-w-7xl mx-auto">
            <h2 
              className="text-4xl md:text-5xl font-bold text-center mb-12"
              style={{ fontFamily: 'Rowdies, cursive' }}
            >
              {sectionTitles.all_specialties || 'All Our Specialties'}
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {allSpecialties.map((specialty) => (
                <div
                  key={specialty.id}
                  className="text-center bg-white p-8 rounded-3xl shadow-lg hover:shadow-xl transition-all hover:scale-105"
                >
                  <OptimizedImage
                    src={specialty.image_url}
                    alt={specialty.name}
                    className="w-48 h-48 mx-auto object-contain mb-6"
                    width={192}
                    height={192}
                  />
                  <h3 className="text-2xl font-black mb-4">{specialty.name}</h3>
                  {specialty.price && (
                    <p className="text-2xl font-bold text-pink-600 mt-2">${specialty.price.toFixed(2)}</p>
                  )}
                  {specialty.description && (
                    <p className="text-sm text-gray-600 mt-2 whitespace-pre-line">{specialty.description}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Weekly Drawing Winners Section - Show when there's a current winner */}
      {winners.current_winner && (
        <section id="weekly-winners" className="py-16 px-6 bg-gradient-to-b from-pink-50 to-white">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <span className="inline-block bg-[#E91E8C] text-white px-4 py-1 rounded-full text-sm font-bold mb-4">
                🏆 WEEKLY DRAWING
              </span>
              <h2 
                className="text-4xl md:text-5xl font-bold"
                style={{ fontFamily: 'Rowdies, cursive' }}
              >
                Our Winners
              </h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 items-start">
              {/* Current Winner - With photo or placeholder */}
              <div className="bg-gradient-to-br from-pink-100 to-pink-50 p-8 rounded-3xl border-4 border-[#E91E8C] shadow-xl">
                <div className="text-center">
                  <div className="text-6xl mb-4">🎉</div>
                  <h3 className="text-2xl font-black mb-2">This Week&apos;s Winner!</h3>
                  {winners.current_winner.image_url ? (
                    <div className="w-32 h-32 mx-auto rounded-full overflow-hidden border-4 border-[#E91E8C] mb-4">
                      <OptimizedImage
                        src={winners.current_winner.image_url}
                        alt={winners.current_winner.name}
                        className="w-full h-full object-cover"
                        width={128}
                        height={128}
                      />
                    </div>
                  ) : (
                    <div className="w-32 h-32 mx-auto rounded-full bg-gradient-to-br from-pink-200 to-purple-200 border-4 border-[#E91E8C] mb-4 flex items-center justify-center">
                      <div className="text-center">
                        <span className="text-3xl">📸</span>
                        <p className="text-xs text-gray-500 font-medium mt-1">Photo Coming Soon</p>
                      </div>
                    </div>
                  )}
                  <p className="text-3xl font-black text-[#E91E8C]">{winners.current_winner.name}</p>
                  {winners.current_winner.prize && (
                    <p className="text-lg text-gray-600 mt-2">Won: {winners.current_winner.prize}</p>
                  )}
                  {/* Week Date */}
                  <p className="text-xs text-gray-400 mt-3">
                    {(() => {
                      const date = winners.current_winner.win_date ? new Date(winners.current_winner.win_date) : (winners.current_winner.created_at ? new Date(winners.current_winner.created_at) : new Date());
                      const weekStart = new Date(date);
                      weekStart.setDate(date.getDate() - date.getDay());
                      const weekEnd = new Date(weekStart);
                      weekEnd.setDate(weekStart.getDate() + 6);
                      const month = weekStart.toLocaleString('default', { month: 'long' });
                      return `Week of ${month} ${weekStart.getDate()}-${weekEnd.getDate()}, ${weekStart.getFullYear()}`;
                    })()}
                  </p>
                </div>
              </div>
              
              {/* Past Winners - Show all with photo or placeholder */}
              {winners.past_winners?.length > 0 && (
                <div className="bg-white p-6 rounded-3xl shadow-lg">
                  <h3 className="text-xl font-black mb-4 text-center">Previous Winners</h3>
                  <div className="space-y-3">
                    {winners.past_winners.slice(0, 4).map((winner, idx) => (
                      <div key={winner.id || idx} className="flex items-center gap-4 p-3 bg-pink-50 rounded-xl">
                        <div className="w-12 h-12 rounded-full overflow-hidden bg-pink-100 flex-shrink-0 flex items-center justify-center">
                          {winner.image_url ? (
                            <img src={winner.image_url} alt={winner.name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-xl">📸</span>
                          )}
                        </div>
                        <div className="flex-1">
                          <p className="font-bold">{winner.name}</p>
                          {winner.prize && <p className="text-sm text-gray-600">{winner.prize}</p>}
                          <p className="text-xs text-gray-400">
                            {(() => {
                              const date = winner.win_date ? new Date(winner.win_date) : (winner.created_at ? new Date(winner.created_at) : new Date());
                              const weekStart = new Date(date);
                              weekStart.setDate(date.getDate() - date.getDay());
                              const weekEnd = new Date(weekStart);
                              weekEnd.setDate(weekStart.getDate() + 6);
                              return `Week of ${weekStart.toLocaleString('default', { month: 'short' })} ${weekStart.getDate()}-${weekEnd.getDate()}`;
                            })()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <Link 
                    to="/winners" 
                    className="block text-center mt-4 text-[#E91E8C] font-bold text-sm hover:underline"
                  >
                    View All Winners →
                  </Link>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Enter Weekly Drawing Form Section */}
      <section 
        id="enter-drawing"
        className="py-16 px-6"
        style={{
          backgroundImage: 'url(/images/misc/storefront-bg.jpg)',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundAttachment: 'fixed'
        }}
      >
        <div className="max-w-3xl mx-auto">
          <div className="bg-white p-8 md:p-10 rounded-3xl shadow-xl">
            <div className="text-center mb-8">
              <h2 
                className="text-3xl md:text-4xl font-bold text-black mb-2"
                style={{ fontFamily: 'Rowdies, cursive' }}
              >
                🎉 Enter Our Weekly Drawing!
              </h2>
              <p className="text-gray-600">Win free ice cream & exclusive offers!</p>
            </div>
            
            <form onSubmit={handleDrawingSubmit} className="space-y-4">
              {/* Honeypot - hidden from users */}
              <input
                type="text"
                name="website_confirm"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
                tabIndex="-1"
                autoComplete="off"
              />

              {/* Customer/Vendor Selection */}
              <div className="bg-pink-50 p-3 rounded-lg">
                <label className="block text-sm font-bold text-gray-700 mb-2">I am a:</label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="entryTypeSpecials"
                      value="customer"
                      checked={entryType === 'customer'}
                      onChange={(e) => setEntryType(e.target.value)}
                      className="w-4 h-4 accent-pink-500"
                    />
                    <span className="font-medium">Customer</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="entryTypeSpecials"
                      value="vendor"
                      checked={entryType === 'vendor'}
                      onChange={(e) => setEntryType(e.target.value)}
                      className="w-4 h-4 accent-pink-500"
                    />
                    <span className="font-medium">Vendor</span>
                  </label>
                </div>
              </div>

              {/* Basic Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="Name *"
                  value={drawingForm.name}
                  onChange={(e) => setDrawingForm({...drawingForm, name: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-pink-500 focus:outline-none"
                  required
                />
                <input
                  type="email"
                  placeholder="Email *"
                  value={drawingForm.email}
                  onChange={(e) => setDrawingForm({...drawingForm, email: e.target.value})}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-pink-500 focus:outline-none"
                  required
                />
              </div>
              
              <input
                type="tel"
                placeholder="Phone Number * (for text notifications)"
                value={drawingForm.phone}
                onChange={(e) => setDrawingForm({...drawingForm, phone: e.target.value})}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-pink-500 focus:outline-none"
                required
              />

              {/* Vendor Fields */}
              {entryType === 'vendor' && (
                <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm font-bold text-gray-700">Vendor Information</p>
                  <input
                    type="text"
                    placeholder="Company Name *"
                    value={drawingForm.companyName}
                    onChange={(e) => setDrawingForm({...drawingForm, companyName: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-pink-500 focus:outline-none"
                  />
                  <input
                    type="tel"
                    placeholder="Company Phone *"
                    value={drawingForm.companyPhone}
                    onChange={(e) => setDrawingForm({...drawingForm, companyPhone: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-pink-500 focus:outline-none"
                  />
                  <input
                    type="text"
                    placeholder="Website URL *"
                    value={drawingForm.websiteUrl}
                    onChange={(e) => setDrawingForm({...drawingForm, websiteUrl: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-pink-500 focus:outline-none"
                  />
                  <input
                    type="text"
                    placeholder="Your Position *"
                    value={drawingForm.position}
                    onChange={(e) => setDrawingForm({...drawingForm, position: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-pink-500 focus:outline-none"
                  />
                  <select
                    value={drawingForm.bestContactMethod}
                    onChange={(e) => setDrawingForm({...drawingForm, bestContactMethod: e.target.value})}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-pink-500 focus:outline-none"
                  >
                    <option value="email">Best Contact: Email</option>
                    <option value="phone">Best Contact: Phone</option>
                    <option value="text">Best Contact: Text</option>
                  </select>
                </div>
              )}

              <textarea
                placeholder="Comment (optional)"
                rows="3"
                value={drawingForm.comment}
                onChange={(e) => setDrawingForm({...drawingForm, comment: e.target.value})}
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-pink-500 focus:outline-none resize-none"
              ></textarea>

              {/* SMS Consent - Required */}
              <div className="bg-teal-50 p-4 rounded-lg border border-teal-200">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={drawingForm.smsConsent}
                    onChange={(e) => setDrawingForm({...drawingForm, smsConsent: e.target.checked})}
                    className="w-5 h-5 mt-0.5 accent-teal-500"
                    required
                  />
                  <span className="text-sm text-gray-700">
                    <strong className="text-teal-700">Required:</strong> I agree to receive text messages from 1st Ave ICP about the weekly drawing, promotions, and special offers. Message frequency varies. Message & data rates may apply. Reply STOP to unsubscribe.
                  </span>
                </label>
              </div>

              <Button 
                type="submit"
                disabled={formSubmitting || !drawingForm.smsConsent}
                className="bg-gradient-to-r from-[#E91E8C] to-[#C4197A] hover:from-[#C4197A] hover:to-[#A01565] text-white font-bold px-12 py-4 rounded-full shadow-lg w-full disabled:opacity-50"
              >
                {formSubmitting ? 'Entering...' : '🎰 Enter Drawing'}
              </Button>
              
              <p className="text-xs text-gray-500 text-center">
                One entry per person. Winners notified via email & text.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* Featured Video - From CMS */}
      {featuredVideo && featuredVideo.video_url && (
        <section 
          className="py-8 sm:py-12 lg:py-16"
          style={{
            backgroundImage: 'url(/images/misc/storefront-bg.jpg)',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat', backgroundPosition: 'center', backgroundAttachment: 'fixed'
          }}
        >
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-6 sm:mb-8">
              <h2 
                className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-black mb-2 sm:mb-3"
                style={{ fontFamily: 'Rowdies, cursive' }}
              >
                {featuredVideo.title || 'Featured Video'}
              </h2>
              {featuredVideo.subtitle && (
                <p className="text-sm sm:text-base lg:text-lg text-gray-700 px-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {featuredVideo.subtitle}
                </p>
              )}
            </div>
            
            {/* Responsive Video Container - Square/Portrait friendly */}
            <div className="relative rounded-xl sm:rounded-2xl overflow-hidden shadow-xl sm:shadow-2xl mx-auto max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg bg-black">
              <video
                ref={videoRef}
                loop
                playsInline
                className="w-full h-auto"
                poster={featuredVideo.poster_url || ''}
                onPlay={() => setVideoPlaying(true)}
                onPause={() => setVideoPlaying(false)}
              >
                <source src={featuredVideo.video_url} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
              
              {/* Play Button Overlay */}
              {!videoPlaying && (
                <div 
                  className="absolute inset-0 flex items-center justify-center bg-black/30 cursor-pointer transition-all hover:bg-black/20 z-10"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (videoRef.current) {
                      const playPromise = videoRef.current.play();
                      if (playPromise !== undefined) {
                        playPromise.catch(() => {
                          console.log('Playback requires user interaction');
                        });
                      }
                    }
                  }}
                >
                  <div className="w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full bg-white/90 flex items-center justify-center shadow-xl hover:scale-110 transition-transform">
                    <svg 
                      className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 text-[#E91E8C] ml-1" 
                      fill="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path d="M8 5v14l11-7z"/>
                    </svg>
                  </div>
                </div>
              )}
              
              {/* Pause overlay when video is playing - click to pause */}
              {videoPlaying && (
                <div 
                  className="absolute inset-0 cursor-pointer z-10"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (videoRef.current) {
                      videoRef.current.pause();
                    }
                  }}
                />
              )}
            </div>
            
            {/* Sound hint - Responsive text */}
            <p className="text-center text-xs sm:text-sm text-gray-500 mt-3 sm:mt-4">
              🔊 Click to play with sound
            </p>
          </div>
        </section>
      )}
    </div>
  );
};

export default Specials;
